import React from "react";
import { useEffect, useRef, useState } from "react";

import {
  Box,
  SpaceBetween,
  Button,
  Form,
  Header,
  ContentLayout,
  Select,
  SegmentedControl,
  SelectProps,
  SegmentedControlProps,
  Grid,
  Spinner,
  Alert,
  Link,
} from "@amzn/awsui-components-console";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import type { RootState } from "../../../redux/store";
import {
  createSite,
  getSite,
  listSites,
  resetSitesState,
  updateSite,
} from "../../../sites/sitesSlice";
import FormContent from "src/forms/FormContent";
import { SITE_CONFIGURATION_CONSTANTS } from "src/forms/constants";
import * as SiteConfigurationForm from "../../../forms/WorkersCompensation/SiteConfigurationForm";
import { resetFormsState, saveForm, setValidating, validateAll } from "src/forms/formsSlice";
import { FormStateType } from "src/forms/types";
import { cloneDeep } from "lodash";
import { resetWizardState } from "src/components/wizard/wizardSlice";
import { ACTION_TYPES, Site } from "../../../sites/types";
import {
  buildSiteFieldOverrides,
  getCountryDependentOverrides,
  getNotificationFieldUpdates,
} from "../../../sites/countryConfig";
import { initialState as initialFormsState } from "../../../forms/formsSlice";
import moment from "moment";

import { SiteTypes } from "../../../sites/constants";
import EnvironmentBanner from "../../../components/environment-banner/EnvironmentBanner";

const SiteConfiguration = () => {
  /**
   * Setup Redux variables and get state
   */
  const dispatch = useAppDispatch();

  const {
    sitesList,
    listingSites,
    adminPanelSite,
    gettingSite,
    creatingSite,
    updatingSite,
    siteApiError,
  } = useAppSelector((state: RootState) => state.sites);
  const initialSitesFormState =
    initialFormsState.allFormStates[SITE_CONFIGURATION_CONSTANTS.FORM_NAME];
  //Redux Forms state
  const { allFormStates, errors, validating } = useAppSelector((state: RootState) => state.forms);
  const formState: FormStateType = cloneDeep(allFormStates[SITE_CONFIGURATION_CONSTANTS.FORM_NAME]);
  const formStateRef = useRef(formState);
  useEffect(() => {
    formStateRef.current = formState;
  });

  //Redux Auth state
  const { user } = useAppSelector((state: RootState) => state.auth);

  /**
   * Local state
   */
  const [site, setSite] = useState<SelectProps.Option | null>(null);
  const [selectedAction, setSelectedAction] = useState<string>(ACTION_TYPES.CREATE);
  const [alert, setAlert] = useState<{
    display: boolean;
    type: "error" | "success" | "info";
    contents: string | React.ReactNode;
  }>({
    display: false,
    contents: "",
    type: "error",
  });
  const [submitedForm, setSubmittedForm] = useState<boolean>(false);

  /**
   * Hook to load sites list, runs on first render
   */
  useEffect(() => {
    dispatch(resetFormsState());
    dispatch(resetWizardState());
    dispatch(resetSitesState());
    dispatch(listSites({ bypassCache: true }));
    resetPageState(selectedAction);
  }, []);

  /**
   * Enable/disable "requested by" fields based on notification values.
   * For edit mode: enables when values differ from the loaded site.
   * For all modes: when notifications are optional (non-US countries),
   * makes "requested by" required only when its parent field has values.
   */

  const notificationAliasList =
    allFormStates[SITE_CONFIGURATION_CONSTANTS.FORM_NAME]?.[
      SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_ALIAS_LIST
    ]?.value;
  const notificationWebhook =
    allFormStates[SITE_CONFIGURATION_CONSTANTS.FORM_NAME]?.[
      SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK
    ]?.value;
  const currentCountryCode =
    allFormStates[SITE_CONFIGURATION_CONSTANTS.FORM_NAME]?.[SITE_CONFIGURATION_CONSTANTS.COUNTRY]
      ?.value?.value;
  const prevNotificationStateRef = useRef<string>("");
  useEffect(() => {
    const isEditMode = !!site && !!adminPanelSite && selectedAction !== ACTION_TYPES.CREATE;
    const updates = getNotificationFieldUpdates({
      countryCode: currentCountryCode,
      notificationAliasList,
      notificationWebhook,
      emailNotificationRequestedBy: {
        ...formState[SITE_CONFIGURATION_CONSTANTS.EMAIL_NOTIFICATION_REQUESTED_BY],
      },
      notificationWebhookRequestedBy: {
        ...formState[SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK_REQUESTED_BY],
      },
      isEditMode,
      originalAliasList: adminPanelSite?.notificationAliasList as string[] | undefined,
      originalWebhook: adminPanelSite?.notificationWebhook as string | undefined,
    });

    if (Object.keys(updates).length === 0) {
      return;
    }

    // Only dispatch if the updates actually differ
    const stateKey = JSON.stringify({
      country: currentCountryCode,
      aliases: Array.isArray(notificationAliasList)
        ? notificationAliasList.map((item: any) => item.value)
        : [],
      webhook: notificationWebhook,
    });
    if (stateKey === prevNotificationStateRef.current) {
      return;
    }
    prevNotificationStateRef.current = stateKey;

    dispatch(
      saveForm({
        formName: SITE_CONFIGURATION_CONSTANTS.FORM_NAME,
        formState: { ...formState, ...updates },
      })
    );
  }, [notificationAliasList, notificationWebhook, currentCountryCode]);

  /**
   * Hook to load site information when current site changes
   */
  useEffect(() => {
    if (!adminPanelSite) {
      return;
    }
    const tempFormState = { ...cloneDeep(formState) };
    const countryCode = adminPanelSite.country || "US";

    for (const key in tempFormState) {
      const currentSiteValue = adminPanelSite[key as keyof typeof adminPanelSite];
      const overrides = buildSiteFieldOverrides(key, currentSiteValue, countryCode);
      tempFormState[key] = {
        ...tempFormState[key],
        value: overrides.value ?? currentSiteValue,
        disabled: overrides.disabled ?? false,
        ...(overrides.options && { options: overrides.options }),
      };
    }

    // Apply country-dependent overrides (regions, postal code, notifications)
    const countryOverrides = getCountryDependentOverrides(countryCode);
    for (const key in countryOverrides) {
      if (tempFormState[key]) {
        tempFormState[key] = {
          ...tempFormState[key],
          ...countryOverrides[key],
        };
      }
    }

    dispatch(
      saveForm({
        formName: SITE_CONFIGURATION_CONSTANTS.FORM_NAME,
        formState: tempFormState,
      })
    );
  }, [adminPanelSite]);

  /**
   * Hook to apply country-dependent overrides when the country dropdown changes.
   */
  useEffect(() => {
    if (!currentCountryCode) {
      return;
    }
    const tempFormState = { ...cloneDeep(formStateRef.current) };
    const countryOverrides = getCountryDependentOverrides(currentCountryCode);
    for (const key in countryOverrides) {
      if (tempFormState[key]) {
        tempFormState[key] = {
          ...tempFormState[key],
          ...countryOverrides[key],
        };
      }
    }
    dispatch(
      saveForm({
        formName: SITE_CONFIGURATION_CONSTANTS.FORM_NAME,
        formState: tempFormState,
      })
    );
  }, [currentCountryCode]);

  /**
   * Hook to submit site
   */
  useEffect(() => {
    if (validating || !submitedForm) {
      return;
    }
    if (errors?.length) {
      setAlert({
        display: true,
        contents: "Please fix the errors in the form and try again",
        type: "error",
      });
      setSubmittedForm(false);
      return;
    }
    submitSite();
  }, [validating, submitedForm]);

  /**
   * Hook to show alert on success or error
   */
  useEffect(() => {
    if (!submitedForm) {
      return;
    }
    if (
      (creatingSite && selectedAction === ACTION_TYPES.CREATE) ||
      (updatingSite && selectedAction === ACTION_TYPES.EDIT)
    ) {
      return;
    }
    setSubmittedForm(false);
    if (siteApiError?.error) {
      const errorContent = siteApiError.error.errors
        ? siteApiError.error.errors.map((err: { fieldName?: string; errorMessage: string }) =>
            err.fieldName ? `${err.fieldName}: ${err.errorMessage}` : err.errorMessage
          )
        : [siteApiError.error.message || "An unknown error occurred"];
      setAlert({
        display: true,
        contents: (
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {errorContent.map((msg: string, i: number) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        ),
        type: "error",
      });

      return;
    }
    setAlert({
      display: true,
      contents: (
        <span>
          {selectedAction === ACTION_TYPES.CREATE
            ? "Site created successfully!"
            : "Site updated successfully!"}
        </span>
      ),
      type: "success",
    });
    resetPageState(selectedAction);
  }, [siteApiError, creatingSite, updatingSite]);

  const onSelectSite = (selectedOption: SelectProps.Option) => {
    setSite(selectedOption);
    dispatch(
      getSite({
        code: selectedOption.value!,
        type: SiteTypes.ADMIN_PANEL_SITE,
      })
    );
    setAlert({ display: false, contents: "", type: "info" });
  };
  const onSiteRefresh = () => {
    dispatch(listSites({ bypassCache: true }));
    setAlert({ display: false, contents: "", type: "info" });
  };
  const onSelectFunction = (selectedId: string) => {
    setSelectedAction(selectedId);
    resetPageState(selectedId);
    setAlert({ display: false, contents: "", type: "info" });
  };

  const resetPageState = (action: string) => {
    dispatch(resetFormsState());
    if (action === ACTION_TYPES.EDIT) {
      setDisabledInForm(true, initialSitesFormState);
    } else {
      setDisabledInForm(false, {
        ...initialSitesFormState,
      });
    }
    setSite(null);
  };

  const setDisabledInForm = (disabled: boolean, formStateToUpdate: FormStateType) => {
    const tempFormState = { ...cloneDeep(formStateToUpdate) };
    for (const key in tempFormState) {
      tempFormState[key].disabled = disabled;
    }
    dispatch(
      saveForm({
        formName: SITE_CONFIGURATION_CONSTANTS.FORM_NAME,
        formState: tempFormState,
      })
    );
  };

  const submitSite = () => {
    const tempFormState = { ...cloneDeep(formState) };
    const siteToSubmit: Site = {};
    for (const key in tempFormState) {
      if (
        SITE_CONFIGURATION_CONSTANTS.STATE === key ||
        SITE_CONFIGURATION_CONSTANTS.COUNTRY === key
      ) {
        siteToSubmit[key] = tempFormState[key].value.value;
        continue;
      }
      if (SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_ALIAS_LIST === key) {
        siteToSubmit[key] = tempFormState[key].value.map((entry: any) => {
          return entry.value;
        });
        continue;
      }
      siteToSubmit[key as keyof Site] = tempFormState[key].value;
    }
    siteToSubmit[SITE_CONFIGURATION_CONSTANTS.EMAIL_NOTIFICATION_UPDATED_AT_TIMESTAMP] =
      moment().unix();

    siteToSubmit[SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK_UPDATED_AT_TIMESTAMP] =
      moment().unix();

    siteToSubmit[SITE_CONFIGURATION_CONSTANTS.EMAIL_NOTIFICATION_REQUESTED_BY] =
      tempFormState[SITE_CONFIGURATION_CONSTANTS.EMAIL_NOTIFICATION_REQUESTED_BY]?.value || user.id;

    siteToSubmit[SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK_REQUESTED_BY] =
      tempFormState[SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK_REQUESTED_BY]?.value ||
      user.id;
    siteToSubmit[SITE_CONFIGURATION_CONSTANTS.LAST_MODIFIED_BY] = user.id || "";
    if (selectedAction === ACTION_TYPES.CREATE) {
      dispatch(createSite({ site: siteToSubmit as Site }));
    }
    if (selectedAction === ACTION_TYPES.EDIT) {
      dispatch(updateSite({ site: siteToSubmit as Site }));
    }
  };
  const onSubmit = () => {
    setAlert({ display: false, contents: "", type: "success" });
    dispatch(setValidating({ value: true }));
    dispatch(
      validateAll({
        formName: SITE_CONFIGURATION_CONSTANTS.FORM_NAME,
        formState,
      })
    );
    setSubmittedForm(true);
  };
  const renderAlert = () => {
    return alert.display ? (
      <Box padding={{ bottom: "m" }}>
        <Alert type={alert.type}>{alert.contents}</Alert>
      </Box>
    ) : null;
  };
  const renderSiteSelect = () => {
    return selectedAction === ACTION_TYPES.EDIT ? (
      <React.Fragment>
        <Box data-testid="site-configuration-select-site-wrapper">
          <Select
            selectedOption={site}
            options={sitesList}
            filteringType="auto"
            virtualScroll={true}
            statusType={listingSites ? "loading" : "finished"}
            placeholder="Select a Site to Edit"
            onChange={({ detail }: { detail: SelectProps.ChangeDetail }) =>
              onSelectSite(detail.selectedOption)
            }
            loadingText="Loading Sites"
            key="site-configuration-site-select"
            data-testid="site-configuration-select-site"
          />
        </Box>
        <Button variant="normal" onClick={onSiteRefresh} loading={listingSites}>
          Refresh Site List
        </Button>
      </React.Fragment>
    ) : null;
  };
  const renderForm = () => {
    return gettingSite || creatingSite || updatingSite ? (
      <Box textAlign="center">
        <Spinner size="large" />{" "}
      </Box>
    ) : (
      <Form key="site-configuration-form" errorText="" errorIconAriaLabel="">
        <FormContent
          formName={SITE_CONFIGURATION_CONSTANTS.FORM_NAME}
          formConfig={SiteConfigurationForm.formConfig}
          formValidation={SiteConfigurationForm.formValidation}
          data-testid="site-configuration-form"
          key="site-configuration-form"
        />
      </Form>
    );
  };
  return (
    <ContentLayout
      header={
        <Header
          description={
            <span>
              Please refer to{" "}
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href="https://w.amazon.com/bin/view/Treasury/TreasuryTech/ClaimIntake/ProductAdmin/SiteManagementRunbook/"
              >
                Site Management Runbook
              </Link>{" "}
              for guidance.
            </span>
          }
          actions={
            <SegmentedControl
              selectedId={selectedAction}
              onChange={({ detail }: { detail: SegmentedControlProps.ChangeDetail }) =>
                onSelectFunction(detail.selectedId)
              }
              label="Default segmented control"
              options={[
                { text: "Edit Site", id: ACTION_TYPES.EDIT },
                { text: "Add Site", id: ACTION_TYPES.CREATE },
              ]}
              key="site-configuration-create-edit"
              data-testid="site-configuration-create-edit"
            />
          }
        >
          Site Configuration Tool
        </Header>
      }
      maxContentWidth={1000}
    >
      <SpaceBetween direction="vertical" size="m">
        <EnvironmentBanner />
        {renderAlert()}

        <Grid
          gridDefinition={
            selectedAction === ACTION_TYPES.EDIT ? [{ colspan: 5 }, { colspan: 3 }] : []
          }
          data-testid="site-configuration-actions-grid"
        >
          {renderSiteSelect()}
        </Grid>
        {renderForm()}

        <Box
          key="site-configuration-buttons"
          float="right"
          data-testid="site-configuration-buttons"
        >
          <SpaceBetween key="site-configuration-buttons-spacer" direction="horizontal" size="m">
            <Button
              key="site-configuration-submit"
              variant="primary"
              disabled={
                listingSites || gettingSite || (!site && selectedAction === ACTION_TYPES.EDIT)
              }
              onClick={() => onSubmit()}
            >
              {selectedAction === ACTION_TYPES.EDIT ? "Submit Changes" : "Create Site"}
            </Button>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </ContentLayout>
  );
};
export default SiteConfiguration;
